import { useEffect, useState } from "react";

import { ObjectiveGroups } from "../components/ObjectiveGroups";
import { ObjectiveRangePicker } from "../components/ObjectiveRangePicker";
import { EmptyCard, QueryErrorState, TabHeading, TabSkeleton } from "../components/tab-states";
import { useObjectives, type ObjectivesRange } from "../data/queries";

/** Profitability by objective and campaign type, over a reporting window. */
export function ObjectivesTab() {
  const [range, setRange] = useState<ObjectivesRange | null>(null);
  const [maxFactDate, setMaxFactDate] = useState<string | null>(null);
  const objectives = useObjectives(range);
  const summary = objectives.data?.summary ?? [];
  const detail = objectives.data?.detail ?? [];
  const window = objectives.data?.window ?? null;

  useEffect(() => {
    if (window?.max_fact_date) setMaxFactDate(window.max_fact_date);
  }, [window?.max_fact_date]);

  return (
    <div className="flex flex-col gap-4">
      <TabHeading
        title="Objectives"
        description="Profitability by objective and campaign type across the selected reporting window."
      />
      <ObjectiveRangePicker maxFactDate={maxFactDate} onChange={setRange} />
      {window && (
        // The maturing-attribution line is not a footnote: the last fortnight of
        // sales is still arriving, so a reader comparing the tail of this window
        // against the middle is comparing a partial number with a complete one.
        <p className="text-xs text-muted-foreground">
          Window {window.start} to {window.end}. Sales after {window.attribution_mature_through} use 14 day
          attribution that is still maturing.
        </p>
      )}
      {objectives.isPending ? (
        <TabSkeleton />
      ) : objectives.isError ? (
        <QueryErrorState
          title="Objectives unavailable"
          message="The edge did not return the required objectives section."
          onRetry={() => objectives.refetch()}
        />
      ) : summary.length === 0 ? (
        <EmptyCard label="No objectives set." />
      ) : (
        <ObjectiveGroups summary={summary} detail={detail} />
      )}
    </div>
  );
}
